import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Store, Upload, Loader2, X } from "lucide-react";
import { api, uploadImage, assetUrl, errMsg } from "@/lib/api";
import { setBrandCache } from "@/lib/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const FIELDS = [
  ["store_name", "Store name", "Royal Gems & Jewellers"],
  ["tagline", "Tagline", "Certified diamonds since 1985"],
  ["city", "City", "Islampur"],
  ["phone", "Phone", "+91 80 4000 1234"],
  ["whatsapp", "WhatsApp", "+91 98000 12345"],
  ["email", "Email", "care@royalgems.com"],
  ["website", "Website", "https://royalgems.com"],
  ["facebook", "Facebook page link", "https://facebook.com/yourstore"],
  ["instagram", "Instagram page link", "https://instagram.com/yourstore"],
  ["google_review", "Google review link", "https://g.page/r/…/review"],
  ["maps_link", "Google Maps link", "https://maps.app.goo.gl/…"],
  ["cert_prefix", "Certificate no. prefix", "JWC  →  JWC-2026-00001"],
];

export default function BrandDialog({ onSaved, label = "Store profile" }) {
  const [open, setOpen] = useState(false);
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [up, setUp] = useState(false);
  const ref = useRef(null);

  useEffect(() => { if (open) api.get("/brand").then((r) => setB({ ...r.data, cert_prefix: r.data.cert_prefix || "JWC" })); }, [open]);
  const set = (k) => (e) => setB({ ...b, [k]: e.target.value });

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUp(true);
    try { setB((x) => ({ ...x, logo: "" })); const url = await uploadImage(f); setB((x) => ({ ...x, logo: url })); } catch (err) { toast.error(errMsg(err)); } finally { setUp(false); }
  };
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/brand", b); setBrandCache(data); toast.success("Store profile saved"); setOpen(false); onSaved?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="store-profile-button"
        className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-3 py-1.5 rounded-md border border-slate-800 hover:border-slate-600 transition-colors">
        <Store size={14} /> {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-800" data-testid="store-profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Store branding</DialogTitle>
            <DialogDescription>Shown to customers on the public certificate page they open by scanning the QR code.</DialogDescription>
          </DialogHeader>
          {!b ? <Loader2 className="animate-spin text-amber-400" /> : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-lg bg-slate-950 border border-slate-800 grid place-items-center overflow-hidden" data-testid="brand-logo-preview">
                  {b.logo ? <img src={assetUrl(b.logo)} alt="" className="w-full h-full object-contain" /> : <Store size={20} className="text-slate-600" />}
                </div>
                <button type="button" onClick={() => ref.current?.click()} className="cf-btn-ghost h-9 text-xs" data-testid="brand-logo-upload-button">
                  {up ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {b.logo ? "Replace logo" : "Upload logo"}
                </button>
                {b.logo && <button type="button" onClick={() => setB({ ...b, logo: "" })} className="cf-btn-ghost h-9 px-2" data-testid="brand-logo-remove"><X size={14} /></button>}
                <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="brand-logo-file-input" />
              </div>
              {FIELDS.map(([k, label, ph]) => (
                <label key={k} className="block space-y-1">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{label}</span>
                  <input className="cf-input h-9 text-sm" value={b[k] || ""} placeholder={ph} onChange={set(k)} data-testid={`brand-${k}-input`} />
                </label>
              ))}
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-1.5">
                <p className="text-[10px] font-mono uppercase tracking-wider text-amber-300">Customer check page</p>
                <div className="flex gap-2">
                  <input readOnly value={`${window.location.origin}/verify`} className="cf-input h-8 text-xs font-mono" data-testid="brand-check-url" />
                  <button type="button" className="cf-btn-ghost h-8 text-xs shrink-0" data-testid="brand-check-url-copy"
                    onClick={() => navigator.clipboard.writeText(`${window.location.origin}/verify`).then(() => toast.success("Link copied"), () => toast.error("Copy blocked — select the link and copy it manually"))}>Copy</button>
                </div>
                <p className="text-[11px] text-slate-500">Share this link (or print it on cards) so customers can verify by certificate number.</p>
              </div>
              <label className="block space-y-1">
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Address</span>
                <textarea rows={2} className="cf-input text-sm" value={b.address || ""} placeholder="MG Road, Bengaluru 560001" onChange={set("address")} data-testid="brand-address-input" />
              </label>
            </div>
          )}
          <DialogFooter>
            <button className="cf-btn-ghost h-9 text-xs" onClick={() => setOpen(false)} data-testid="brand-cancel-button">Cancel</button>
            <button className="cf-btn-primary h-9 text-xs px-4" onClick={save} disabled={busy || up || !b} data-testid="brand-save-button">
              {busy && <Loader2 size={14} className="animate-spin" />} Save profile
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
